import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { TrackingApiService } from '../services/tracking-api.service';
import { ToasterService } from '../../../../core/services/toaster.service';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { parseApiDate } from '../tracking-format.util';
import type { BlacklistedIp } from '../tracking-demo.models';

/** Blacklist tab: list active blocked IPs and remove (un-blacklist) them. */
@Component({
  selector: 'app-blacklist-manager',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-200">Blacklisted IPs</h3>
        <button type="button" (click)="load()" class="text-xs text-indigo-500 hover:underline">Refresh</button>
      </div>
      <div class="overflow-x-auto rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">
        <table class="min-w-full text-sm">
          <thead class="bg-slate-50 dark:bg-white/5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-ink-soft/50">
            <tr>
              <th class="px-3 py-2 text-left font-medium">IP Address</th>
              <th class="px-3 py-2 text-left font-medium">Reason</th>
              <th class="px-3 py-2 text-left font-medium">Blocked By</th>
              <th class="px-3 py-2 text-left font-medium">Session</th>
              <th class="px-3 py-2 text-left font-medium">Blocked At</th>
              <th class="px-3 py-2 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-line/60">
            @for (ip of rows(); track ip.id) {
              <tr class="hover:bg-slate-50 dark:hover:bg-line/40">
                <td class="px-3 py-2 font-mono text-xs">{{ ip.ip_address }}</td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-300">{{ ip.reason || '—' }}</td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-300">{{ ip.blocked_by || '—' }}</td>
                <td class="px-3 py-2 font-mono text-[11px] text-gray-400">{{ ip.session_id || '—' }}</td>
                <td class="px-3 py-2 tabular-nums text-gray-500">{{ timeOf(ip.created_at) }}</td>
                <td class="px-3 py-2 text-right">
                  <button type="button" (click)="remove(ip)"
                    class="rounded-md px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10">Un-blacklist</button>
                </td>
              </tr>
            } @empty {
              <tr><td colspan="6" class="px-3 py-10 text-center text-gray-400">{{ loading() ? 'Loading…' : 'No blacklisted IPs.' }}</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  `,
})
export class BlacklistManagerComponent implements OnInit {
  private readonly api = inject(TrackingApiService);
  private readonly dialog = inject(MatDialog);
  private readonly toaster = inject(ToasterService);

  readonly rows = signal<BlacklistedIp[]>([]);
  readonly loading = signal(false);

  /** Local date+time (backend stores naive UTC — pinned to UTC before formatting). */
  timeOf(iso?: string | null): string {
    const d = parseApiDate(iso);
    return d
      ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      : '—';
  }

  ngOnInit(): void { this.load(); }

  async load(): Promise<void> {
    this.loading.set(true);
    try {
      const res = await this.api.getBlacklist(false);
      this.rows.set(res.items || []);
    } catch {
      /* handled by api */
    } finally {
      this.loading.set(false);
    }
  }

  async remove(ip: BlacklistedIp): Promise<void> {
    const ok = await firstValueFrom(this.dialog.open(ConfirmationModalComponent, {
      width: '420px',
      data: {
        title: 'Remove from blacklist?',
        description: `${ip.ip_address} will be allowed to start demos again.`,
        variant: 'primary',
        confirmLabel: 'Un-blacklist',
      },
    }).afterClosed());
    if (!ok) return;
    try {
      await this.api.removeBlacklistIp(ip.ip_address);
      this.toaster.success(`${ip.ip_address} removed from blacklist`);
      this.load();
    } catch {
      this.toaster.error('Failed to remove IP');
    }
  }
}
